import { PageHero } from '@components/common';
import { MapPin, Clock, CheckCircle2, Briefcase } from 'lucide-react';

export default function SecurityConsultantPage() {
  return (
    <>
      <PageHero
        title="Security Consultant"
        eyebrow="Careers"
        seoTitle="Security Consultant | The CISO Network"
        seoDescription="Join us as a Security Consultant. Support our fractional CISOs in delivering security assessments, compliance programmes, and risk management."
      />

      {/* Role Overview */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="flex flex-wrap gap-4 mb-8 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              Remote UK
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Full-time
            </span>
            <span className="flex items-center gap-1">
              <Briefcase className="h-4 w-4" />
              3-6 years experience
            </span>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                About the Role
              </h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-muted-foreground mb-4">
                  As a Security Consultant at The CISO Network, you'll work alongside our fractional
                  CISOs to deliver high-impact security programmes for our diverse client base. This
                  is a hands-on role where you'll gain exposure to multiple industries, security
                  challenges, and organisational contexts.
                </p>
                <p className="text-muted-foreground mb-4">
                  You'll be the execution engine behind our CISOs' strategic vision - conducting
                  assessments, implementing controls, documenting policies, and ensuring our clients
                  achieve their security objectives.
                </p>
                <p className="text-muted-foreground mb-8">
                  This role offers exceptional career development for security professionals looking
                  to broaden their experience and eventually step into CISO positions themselves.
                </p>

                <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                  Key Responsibilities
                </h3>
                <ul className="space-y-3 mb-8">
                  {[
                    'Conduct security assessments and gap analyses across client environments',
                    'Develop and implement security policies, procedures, and standards',
                    'Support compliance programmes (SOC 2, ISO 27001, GDPR) including evidence collection',
                    'Perform risk assessments and assist with risk treatment planning',
                    'Create security documentation and board reporting materials',
                    'Support incident response activities when required',
                    'Maintain and update client security documentation and evidence libraries',
                    'Collaborate with client IT and security teams on control implementation',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                  What We're Looking For
                </h3>
                <ul className="space-y-3 mb-8">
                  {[
                    '3-6 years of experience in cybersecurity, consulting, or IT risk management',
                    'Hands-on experience with security frameworks (ISO 27001, SOC 2, NIST)',
                    'Understanding of cloud security principles (AWS, Azure, or GCP)',
                    'Strong written and verbal communication skills',
                    'Ability to work independently and manage multiple client engagements',
                    'Detail-oriented with excellent documentation skills',
                    'Relevant certifications (CISSP, CISM, Security+, or working towards)',
                    'Experience with compliance audits or security assessments preferred',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-heading font-bold text-foreground mb-4">Why Join Us</h3>
                <p className="text-muted-foreground mb-4">
                  This role offers unparalleled exposure to how security works across dozens of
                  organisations. You'll learn from experienced CISOs, develop broad expertise, and
                  build a network that will serve your entire career.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Competitive salary: £50,000-£70,000 depending on experience',
                    'Remote-first culture with flexible working arrangements',
                    '£3,000 annual professional development budget',
                    'Exposure to diverse clients and security challenges',
                    'Clear progression path to Senior Consultant and CISO roles',
                    'Mentorship from our network of experienced fractional CISOs',
                    'Regular training and knowledge-sharing sessions',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-4">
                  Apply for this Role
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  This position is currently closed. We are not accepting applications at this time,
                  but please check back in the future.
                </p>
                <button
                  disabled
                  className="w-full px-6 py-3 rounded-lg bg-muted text-muted-foreground font-medium cursor-not-allowed"
                >
                  Position Closed
                </button>
              </div>

              <div className="p-6 rounded-xl bg-background border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-4">Questions?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Want to learn more about the role before applying? We're happy to have an informal
                  chat.
                </p>
                <a
                  href="mailto:careers@thecisonetwork.com?subject=Question: Security Consultant Role"
                  className="text-teal hover:underline text-sm"
                >
                  Contact our recruitment team
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
